import clientSide from "@/helpers/clientSide";
import { getCookieValue, parseSCCookie } from "@/lib/cookies";
import type {
  Address,
  LocalizationType,
  LocalizedCustomerState,
  SessionStorageLocalizedAddress,
} from "@/types/common";
import type { SCCookieFields } from "@/types/cookies";
import { useEffect, useState } from "react";

const getSessionStorageAddress = (): Address | null => {
  if (!clientSide) {
    return null;
  }

  try {
    const localizedAddressStr = sessionStorage.getItem("localizedAddress");

    if (!localizedAddressStr) {
      return null;
    }

    const localizedAddress: SessionStorageLocalizedAddress =
      JSON.parse(localizedAddressStr);

    if (!localizedAddress?.address) {
      return null;
    }

    const { address } = localizedAddress;

    let street: string | undefined = undefined;

    if (address.streetAddress1) {
      street = address.streetAddress1;

      if (address.unitType && address.unitNumber) {
        street += ` ${address.unitType} ${address.unitNumber}`;
      }
    }

    if (!address.city && !street) {
      return null;
    }

    // Map to common Address interface
    return {
      street: street,
      city: address.city,
      state: address.state,
      zipCode: address.zipCode,
      addressCode: address.locationId || undefined,
    };
  } catch (error) {
    console.error("Failed to parse sessionStorage localizedAddress:", error);
    return null;
  }
};

const formatLocationDisplay = (location: Address): string => {
  const { street, city, zipCode } = location;

  if (street && street !== "undefined") {
    if (city) {
      return `Shopping from ${street.toUpperCase()}, ${city.toUpperCase()} ${zipCode || ""}`.trim();
    }
    return `Shopping from ${street.toUpperCase()} ${zipCode || ""}`.trim();
  }

  if (city) {
    return `Shopping from ${city.toUpperCase()}`;
  }

  return "";
};

const areAddressesEqual = (
  currentAddress: Address | null,
  nextAddress: Address | null
) => {
  if (currentAddress === nextAddress) {
    return true;
  }

  if (!currentAddress || !nextAddress) {
    return false;
  }

  return (
    currentAddress.street === nextAddress.street &&
    currentAddress.city === nextAddress.city &&
    currentAddress.state === nextAddress.state &&
    currentAddress.zipCode === nextAddress.zipCode &&
    currentAddress.addressCode === nextAddress.addressCode &&
    currentAddress.geoEnabled === nextAddress.geoEnabled
  );
};

const areLocalizedStatesEqual = (
  currentState: LocalizedCustomerState,
  nextState: LocalizedCustomerState
) =>
  currentState.isLocalized === nextState.isLocalized &&
  currentState.localizationType === nextState.localizationType &&
  currentState.isLoading === nextState.isLoading &&
  currentState.salesSessionId === nextState.salesSessionId &&
  currentState.error === nextState.error &&
  areAddressesEqual(currentState.customerAddress, nextState.customerAddress);

export const useLandingPage = () => {
  const [state, setState] = useState<LocalizedCustomerState>({
    isLocalized: false,
    localizationType: "none",
    isLoading: true,
    customerAddress: null,
    salesSessionId: null,
  });

  useEffect(() => {
    const checkLocalizationState = () => {
      try {
        const scCookie = getCookieValue("SC");
        const scData = scCookie
          ? (parseSCCookie(scCookie) as SCCookieFields)
          : null;
        const salesSessionId = scData?.["RC.SP"] || null;
        const geoCity = scData?.CTY?.toUpperCase() || null;
        const geoEnabled = scData?.GEO?.toUpperCase() === "TRUE";

        const sessionStorageAddress = getSessionStorageAddress();

        // Determine localization type using priority hierarchy
        let localizationType: LocalizationType = "none";
        let customerAddress: Address | null = null;
        let isLocalized = false;

        // Priority 1: Fully localized (RC.SP + sessionStorage address)
        if (salesSessionId && sessionStorageAddress) {
          localizationType = "full";
          isLocalized = true;
          customerAddress = {
            street: sessionStorageAddress.street,
            city: sessionStorageAddress.city,
            state: sessionStorageAddress.state,
            zipCode: sessionStorageAddress.zipCode,
            addressCode: sessionStorageAddress.addressCode,
            geoEnabled: geoEnabled,
          };
        }
        // Priority 2: Geo-located (city from SC cookie only)
        else if (geoCity && geoEnabled) {
          localizationType = "geo";
          isLocalized = true;
          customerAddress = {
            city: geoCity,
            geoEnabled: true,
          };
        }
        // Priority 3: Not localized (fallback)
        else {
          localizationType = "none";
          isLocalized = false;
          customerAddress = null;
        }

        const nextState: LocalizedCustomerState = {
          isLocalized,
          localizationType,
          isLoading: false,
          customerAddress,
          salesSessionId,
        };

        setState((currentState) =>
          areLocalizedStatesEqual(currentState, nextState)
            ? currentState
            : nextState
        );
      } catch (err) {
        console.error("Failed to parse location information:", err);
        const nextState: LocalizedCustomerState = {
          isLocalized: false,
          localizationType: "none",
          isLoading: false,
          customerAddress: null,
          salesSessionId: null,
          error: "Failed to parse location information",
        };

        setState((currentState) =>
          areLocalizedStatesEqual(currentState, nextState)
            ? currentState
            : nextState
        );
      }
    };

    checkLocalizationState();

    if (clientSide) {
      const intervalId = setInterval(checkLocalizationState, 500);

      const timeoutId = setTimeout(() => {
        clearInterval(intervalId);
      }, 30000);

      return () => {
        clearInterval(intervalId);
        clearTimeout(timeoutId);
      };
    }
  }, []);

  const locationDisplay = state.customerAddress
    ? formatLocationDisplay(state.customerAddress)
    : "";

  return {
    ...state,
    locationDisplay,
  };
};
